import { AppId, FCX, usePlatformContext } from "@datassential/platform-ui-lib";
import AutoSizer from "react-virtualized-auto-sizer";
import LineChart from "@components/LineChart/LineChart";
import Table from "@components/new/Table/Table";
import React, { useCallback, useMemo, useState } from "react";
import { sanitizeUrl } from "@helpers";
import { iTableConfigItem } from "@components/new/Table/Table.helpers";
import { transformApiDataToLineChart } from "@apiHooks/useLineChartData";
import { TopGrowingCategory } from "@apiHooks/useHomeGetTopGrowingKeywords";
import HomeSectionSubTitle from "@pages/Home/components/HomeSectionSubTitle";
import HomeSectionHeader from "@pages/Home/components/HomeSectionHeader";
import SliderNav from "@components/new/SliderNav/SliderNav";
import { HOME_TABLE_COL_HIGHLIGHT_STYLE } from "@pages/Home/constants";

const TABLE_COL_DEFAULTS: Pick<iTableConfigItem, 'width' | 'textCentered'> = {
    width: 200,
    textCentered: true,
};

const AUTO_SLIDE_INTERVAL = 10000;

const HomeFoodsMenuGrowthSlide: FCX<{
    data: TopGrowingCategory;
    onPrev: () => void;
    onNext: () => void;
}> = ({
    data,
    onPrev,
    onNext,
}) => {
    const { userInfo: { products } } = usePlatformContext();

    const menuTrends = useMemo(
        () => products[AppId.MenuTrends],
        [products]
    );

    const [isAutoSlideEnabled, setIsAutoSlideEnabled] = useState(true);

    const printValue = useCallback((v: number) => `${v.toFixed(1)}%`, []);
    const printLabel = useCallback((v: number) => `${v}%`, []);

    const allKeywordsMtLink = useMemo(
        () => sanitizeUrl(`${menuTrends.url}${process.env.REACT_APP_MT_BYO_LINK}${encodeURIComponent(
            data?.keywords?.map(k => k.name).join(',') ?? ''
        )}`),
        [data, menuTrends]
    );

    const chartConfig = useMemo(
        () => {
            if (data) {
                return transformApiDataToLineChart(data);
            }

            return null;
        },
        [data]
    )

    const tableConfig = useMemo(
        (): iTableConfigItem[] => {
            if (data) {
                return [
                    {
                        title: "Keyword",
                        Value: ({ index }) => (<>
                            {chartConfig && (
                                <LineChart
                                    config={{
                                        ...chartConfig,
                                        series: [chartConfig.series[index]],
                                    }}
                                    isOnlyLine
                                    baseChartHeight={20}
                                    width={40}
                                    height={20}
                                    printValue={printValue}
                                    printLabel={printLabel}
                                    style={{
                                        transform: 'translateX(-10px)',
                                    }}
                                />
                            )}
                            <a
                                href={allKeywordsMtLink}
                                target="_blank"
                                rel="noreferrer"
                            >
                                {data.keywords?.[index].name}
                            </a>
                        </>),
                    },
                    {
                        title: "Menu Penetration",
                        Value: ({ index }) => {
                            const val = data.keywords?.[index].currentPenetration;
                            return <>{val !== undefined ? `${val.toFixed(1)}%` : "-"}</>;
                        },
                        ...TABLE_COL_DEFAULTS,
                    },
                    {
                        title: "Past 4-Yr Growth",
                        Value: ({ index }) => {
                            const val = data.keywords?.[index].fourYearTrend;
                            return <>{val !== undefined ? `${val > 0 ? '+' : ''}${val.toFixed(1)}%` : "-"}</>;
                        },
                        ...TABLE_COL_DEFAULTS,
                        styles: {
                            cell: HOME_TABLE_COL_HIGHLIGHT_STYLE,
                        },
                    },
                    {
                        title: "Predicted 4-Yr Growth",
                        Value: ({ index }) => {
                            const val = data.keywords?.[index].predictedGrowth;
                            return <>{val !== undefined ? `${val > 0 ? '+' : ''}${val.toFixed(1)}%` : "-"}</>;
                        },
                        ...TABLE_COL_DEFAULTS,
                    },
                ];
            }

            return [];
        },
        [data, allKeywordsMtLink, chartConfig, printValue, printLabel]
    );

    return (
        <div className="HomeFoodsMenuGrowthSlide">
            <SliderNav
                onPrev={onPrev}
                onNext={onNext}
                autoSlideIntervalMs={isAutoSlideEnabled ? AUTO_SLIDE_INTERVAL : 0}
            />
            <HomeSectionHeader>
                <HomeSectionSubTitle>
                    Menu Growth:&nbsp;
                    <div className="appears-left del-1">
                        {data?.topTrendCategoryName}
                    </div>
                </HomeSectionSubTitle>
            </HomeSectionHeader>
            {chartConfig && (
                <div
                    className="HomeFoodsMenuGrowthSlide__inner"
                    onMouseEnter={() => setIsAutoSlideEnabled(false)}
                    onMouseLeave={() => setIsAutoSlideEnabled(true)}
                >
                    <div className="HomeFoodsMenuGrowthSlide__chart">
                        <AutoSizer>
                            {({ height, width }) => (
                                <LineChart
                                    className="appears-top del-2"
                                    config={chartConfig}
                                    width={width}
                                    height={height}
                                    printValue={printValue}
                                    printLabel={printLabel}
                                />
                            )}
                        </AutoSizer>
                    </div>
                    <Table
                        className="appears-top del-4"
                        config={tableConfig}
                        rowsCount={5}
                        hasInnerScroll={false}
                        rowHeight={50}
                        isCopyButtonHidden
                    />
                </div>
            )}
        </div>
    );
};

export default HomeFoodsMenuGrowthSlide;